import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useLocalStorage } from 'usehooks-ts';
import {
  tenantCloneJobDisposition,
  Tenant,
  TenantCloneJob,
  TenantListItem,
  TenantType
} from '@bliks-io/contracts';
import { useListTenants } from '@/pages/admin/api/queries/useTenants';
import {
  useCloneJobs,
  useDiscardCloneJob,
  useDismissCloneJob,
  useRefreshTenantsWhenACloneJobFinishes
} from '@/pages/admin/api/queries/useCloneJobs';
import { useDeleteTenant } from '@/pages/admin/api/mutations/useTenants';
import type { ApiError } from '@/lib/agent.types';
import { translateErrorMessage } from '@/lib/errors';
import {
  Button,
  Badge,
  Page,
  PageContent,
  DataTable,
  Label,
  Switch,
  createActionsColumn,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { CreateTenantDialog } from './CreateTenantDialog';
import { CloneTenantDialog } from './CloneTenantDialog';
import { CloneJobAlert } from './components/CloneJobAlert';
import { CloneJobBadge } from './components/CloneJobBadge';
import { DeleteTenantConfirmDialog } from './components/DeleteTenantConfirmDialog';
import { DiscardCloneConfirmDialog } from './components/DiscardCloneConfirmDialog';
import { TenantTypeBadge, TENANT_TYPE_LABEL_KEYS } from './components/TenantTypeBadge';
import { TenantExpiryBadge, isTenantExpired } from './components/TenantExpiryBadge';

const TYPE_FILTER_OPTIONS: Array<{ value: TenantType | 'all'; labelKey: string }> = [
  { value: 'all', labelKey: 'tenantAdmin.filterAll' },
  { value: 'demo', labelKey: TENANT_TYPE_LABEL_KEYS.demo },
  { value: 'partner', labelKey: TENANT_TYPE_LABEL_KEYS.partner },
  { value: 'customer', labelKey: TENANT_TYPE_LABEL_KEYS.customer }
];

export function TenantListPage() {
  useDocumentTitle('pageTitles.tenants');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useListTenants();
  const { data: cloneJobsData } = useCloneJobs();
  const deleteTenant = useDeleteTenant();
  const discardCloneJob = useDiscardCloneJob();
  // Destructured, not held as an object: react-query returns a fresh result
  // literal every render, while `mutateAsync` is bound once in the mutation
  // observer's constructor. Depending on the object would rebuild every column
  // and every row cell of the table below on every render.
  const { mutateAsync: dismissCloneJob, isPending: isDismissingCloneJob } = useDismissCloneJob();
  // This screen is the one that shows both resources, so it owns the
  // cross-query refresh the clone-jobs hook deliberately does not.
  useRefreshTenantsWhenACloneJobFinishes(cloneJobsData?.jobs);

  const [createTenantOpen, setCreateTenantOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | undefined>(undefined);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloningTenant, setCloningTenant] = useState<Tenant | undefined>(undefined);
  const [deletingTenant, setDeletingTenant] = useState<Tenant | undefined>(undefined);
  const [discardingJob, setDiscardingJob] = useState<TenantCloneJob | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<TenantType | 'all'>('all');
  const [hideExpired, setHideExpired] = useLocalStorage(
    'bliks:datatable:adminTenants:hideExpired',
    false
  );

  const filteredTenants = useMemo(
    () =>
      (data?.tenants ?? []).filter(
        (tenant) =>
          (typeFilter === 'all' || tenant.tenantType === typeFilter) &&
          (!hideExpired || !isTenantExpired(tenant.expiresAt))
      ),
    [data?.tenants, typeFilter, hideExpired]
  );

  // Merged client-side: the clone jobs are their own lightweight endpoint, so
  // `list-tenants` never learns about them.
  const cloneJobsByTenantId = useMemo(
    () => new Map((cloneJobsData?.jobs ?? []).map((job) => [job.tenantId, job])),
    [cloneJobsData?.jobs]
  );

  // BOTH ends of a running copy: `delete-tenant` refuses for the source as well
  // as the target, so leaving the source's actions enabled only moves the
  // failure to after the destructive confirm dialog.
  const tenantIdsBeingCloned = useMemo(
    () =>
      new Set(
        (cloneJobsData?.jobs ?? [])
          .filter((job) => job.status === 'running')
          .flatMap((job) => [job.tenantId, job.sourceTenantId])
      ),
    [cloneJobsData?.jobs]
  );

  // Only clones whose TENANT still needs a decision. A kept tenant's clone
  // succeeded, so a discard is refused by design — those get a row badge with
  // its own dismiss. The partition itself lives in contracts, so this filter and
  // the backend's discard guard cannot disagree about it.
  const jobsNeedingDecision = useMemo(
    () =>
      (cloneJobsData?.jobs ?? []).filter(
        (job) => tenantCloneJobDisposition(job.status) === 'needsDecision'
      ),
    [cloneJobsData?.jobs]
  );

  const handleCreateTenant = () => {
    setEditingTenant(undefined);
    setCreateTenantOpen(true);
  };

  const handleEdit = useCallback((tenant: Tenant) => {
    setEditingTenant(tenant);
    setCreateTenantOpen(true);
  }, []);

  const handleCloneClick = useCallback((tenant: Tenant) => {
    setCloningTenant(tenant);
    setCloneDialogOpen(true);
  }, []);

  const handleDeleteClick = useCallback((tenant: Tenant) => {
    setDeletingTenant(tenant);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deletingTenant) return;

    try {
      await deleteTenant.mutateAsync(deletingTenant._id);
      toast.success(t('tenantAdmin.deleteSuccess'));
      setDeletingTenant(undefined);
    } catch (error) {
      toast.error(t('tenantAdmin.deleteFailed'));
      console.error('Failed to delete tenant:', error);
    }
  };

  const handleViewTenant = useCallback(
    (tenant: Tenant) => {
      navigate({ to: '/admin/tenants/$tenantId', params: { tenantId: tenant._id } });
    },
    [navigate]
  );

  const handleDiscardConfirm = async () => {
    if (!discardingJob) return;

    try {
      const { jobRetained } = await discardCloneJob.mutateAsync(discardingJob.jobId);
      // A partially rolled-back discard keeps its job so the leftovers stay
      // visible — reporting a clean rollback that did not happen is worse than
      // the leftovers themselves.
      if (jobRetained) {
        toast.warning(t('tenantAdmin.cloneDiscardPartial'));
      } else {
        toast.success(t('tenantAdmin.cloneDiscardSuccess'));
      }
      setDiscardingJob(undefined);
    } catch (error) {
      // Renders the backend's own code — a refused discard (the clone finished
      // after all) has to say exactly that, not a generic failure.
      toast.error(translateErrorMessage(error as ApiError, t));
    }
  };

  const handleDismiss = useCallback(
    async (job: TenantCloneJob) => {
      try {
        await dismissCloneJob(job.jobId);
        toast.success(t('tenantAdmin.cloneDismissSuccess'));
      } catch (error) {
        toast.error(translateErrorMessage(error as ApiError, t));
      }
    },
    [dismissCloneJob, t]
  );

  // Define table columns with stable reference using useMemo
  const columns = useMemo<DataTableColumnDef<TenantListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: createColumnHeader(t('tenantAdmin.columnName')),
        meta: { label: t('tenantAdmin.columnName'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => {
          const tenant = row.original;
          const cloneJob = cloneJobsByTenantId.get(tenant._id);
          let logoUrl: string | undefined;

          if (tenant.logo) {
            if (tenant.logo.type === 'svg') {
              logoUrl = tenant.logo.svg;
            } else {
              logoUrl = tenant.logo.thumbnail?.webp || tenant.logo.thumbnail?.png;
            }
          }

          return (
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 shrink-0 flex items-center justify-center">
                {logoUrl && (
                  <img src={logoUrl} alt={tenant.name} className="h-8 w-8 object-contain" />
                )}
              </div>
              <span
                className="font-medium cursor-pointer hover:text-primary hover:underline"
                onClick={() => handleViewTenant(tenant)}
              >
                {tenant.name}
              </span>
              {cloneJob && (
                <CloneJobBadge
                  job={cloneJob}
                  onDismiss={() => handleDismiss(cloneJob)}
                  isDismissing={isDismissingCloneJob}
                />
              )}
            </div>
          );
        },
        enableGlobalFilter: true
      },
      {
        accessorKey: 'slug',
        header: createColumnHeader(t('tenantAdmin.columnSlug')),
        meta: { label: t('tenantAdmin.columnSlug'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => <span className="text-muted-foreground">{row.getValue('slug')}</span>,
        enableGlobalFilter: true
      },
      {
        accessorKey: 'description',
        header: createColumnHeader(t('tenantAdmin.columnDescription')),
        meta: { label: t('tenantAdmin.columnDescription'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => {
          const description = row.original.description;
          if (!description) return null;
          return (
            <span
              className="block max-w-[200px] truncate text-muted-foreground"
              title={description}
            >
              {description}
            </span>
          );
        },
        enableGlobalFilter: true
      },
      {
        accessorKey: 'tenantType',
        header: createColumnHeader(t('tenantAdmin.columnType')),
        meta: { label: t('tenantAdmin.columnType') },
        cell: ({ row }) => <TenantTypeBadge tenantType={row.original.tenantType} />
      },
      {
        accessorKey: 'active',
        header: createColumnHeader(t('tenantAdmin.columnStatus')),
        meta: {
          label: t('tenantAdmin.columnStatus'),
          filterType: 'select',
          filterOptions: [
            { label: t('tenantAdmin.statusActive'), value: 'true' },
            { label: t('tenantAdmin.statusInactive'), value: 'false' }
          ]
        },
        enableColumnFilter: true,
        cell: ({ row }) => {
          const active = row.getValue('active') as boolean;
          return (
            <Badge colorScheme={active ? 'success' : 'error'}>
              {active ? t('tenantAdmin.statusActive') : t('tenantAdmin.statusInactive')}
            </Badge>
          );
        }
      },
      {
        id: 'members',
        accessorFn: (row) => row.memberCount ?? 0,
        header: createColumnHeader(t('tenantAdmin.columnMembers')),
        meta: { label: t('tenantAdmin.columnMembers') },
        cell: ({ row }) => (
          <span className="tabular-nums font-medium">{row.original.memberCount ?? 0}</span>
        )
      },
      {
        id: 'processes',
        accessorFn: (row) => row.processCount ?? 0,
        header: createColumnHeader(t('tenantAdmin.columnProcesses')),
        meta: { label: t('tenantAdmin.columnProcesses') },
        cell: ({ row }) => (
          <span className="tabular-nums font-medium">{row.original.processCount ?? 0}</span>
        )
      },
      {
        accessorKey: 'expiresAt',
        header: createColumnHeader(t('tenantAdmin.columnExpires')),
        meta: { label: t('tenantAdmin.columnExpires') },
        cell: ({ row }) => <TenantExpiryBadge expiresAt={row.original.expiresAt} />
      },
      // Editing, cloning or deleting a tenant mid-copy races a detached worker
      // that would happily re-provision what a delete just revoked.
      createActionsColumn<TenantListItem>({
        onView: handleViewTenant,
        onEdit: handleEdit,
        onClone: handleCloneClick,
        onDelete: handleDeleteClick,
        isRowDisabled: (tenant) => tenantIdsBeingCloned.has(tenant._id)
      })
    ],
    [
      handleEdit,
      handleDeleteClick,
      handleViewTenant,
      handleCloneClick,
      handleDismiss,
      cloneJobsByTenantId,
      tenantIdsBeingCloned,
      isDismissingCloneJob,
      t
    ]
  );

  return (
    <Page>
      <PageContent>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('tenantAdmin.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('tenantAdmin.description')}</p>
          </div>
          <Button onClick={handleCreateTenant}>
            <Plus className="w-4 h-4 mr-2" />
            {t('tenantAdmin.createTenant')}
          </Button>
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {TYPE_FILTER_OPTIONS.map((option) => (
            <Button
              key={option.value}
              size="small"
              variant={typeFilter === option.value ? 'primary' : 'outline'}
              onClick={() => setTypeFilter(option.value)}
            >
              {t(option.labelKey)}
            </Button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Label htmlFor="hide-expired-tenants">{t('tenantAdmin.hideExpired')}</Label>
            <Switch
              id="hide-expired-tenants"
              checked={hideExpired}
              onCheckedChange={setHideExpired}
            />
          </div>
        </div>

        {/* Clones that stopped and need an explicit decision */}
        {jobsNeedingDecision.length > 0 && (
          <div className="mb-4 space-y-3">
            {jobsNeedingDecision.map((job) => (
              <CloneJobAlert
                key={job.jobId}
                job={job}
                onDiscard={() => setDiscardingJob(job)}
                onDismiss={() => handleDismiss(job)}
                isDismissing={isDismissingCloneJob}
              />
            ))}
          </div>
        )}

        {/* Table with built-in search */}
        <DataTable
          columns={columns}
          data={filteredTenants}
          isLoading={isLoading}
          enableGlobalFilter
          enableColumnVisibility
          enableColumnFilters
          enableUrlState
          tableId="adminTenants"
          globalSearchPlaceholder={t('tenantAdmin.searchPlaceholder')}
          initialColumnVisibility={{ slug: false, members: false, processes: false }}
        />

        {/* Dialogs */}
        <CreateTenantDialog
          open={createTenantOpen}
          onOpenChange={setCreateTenantOpen}
          editingTenant={editingTenant}
        />

        {/* Clone Dialog */}
        {cloningTenant && (
          <CloneTenantDialog
            open={cloneDialogOpen}
            onOpenChange={setCloneDialogOpen}
            source={cloningTenant}
          />
        )}

        {/* Delete Confirmation Dialog — mounted conditionally so the typed name
            is destroyed with it, whichever path closes it. */}
        {deletingTenant && (
          <DeleteTenantConfirmDialog
            open
            onOpenChange={(open) => !open && setDeletingTenant(undefined)}
            onConfirm={handleDeleteConfirm}
            isDeleting={deleteTenant.isPending}
            tenantName={deletingTenant.name}
          />
        )}

        {/* Discard Confirmation Dialog — drops the half-built clone's database.
            Mounted conditionally so the typed-name gate is destroyed with it: a
            partial discard keeps its job, and a retry must be typed out again. */}
        {discardingJob && (
          <DiscardCloneConfirmDialog
            open
            onOpenChange={(open) => !open && setDiscardingJob(undefined)}
            onConfirm={handleDiscardConfirm}
            isDiscarding={discardCloneJob.isPending}
            tenantName={discardingJob.tenantName}
          />
        )}
      </PageContent>
    </Page>
  );
}
